import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { message } from 'antd';

import MergeDialog from '@totem/components/vendors/MergeDialog';
import {
  Vendor,
  VendorInput,
  VendorUpdate,
} from '@totem/components/vendors/types';
import VendorLogoContext from '@totem/components/vendors/vendorLogos/vendorLogoContext';
import { isNotNull } from '@totem/utilities/common';
import {
  DEVICES_ENDPOINT,
  UNAUTHENTICATED_VENDORS_ENDPOINT,
} from '@totem/utilities/endpoints';
import { omitNilOrEmpty } from '@totem/utilities/objectUtilities';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

type Props = {
  children?: ReactNode;
};
const VendorLogoContainer = ({ children }: Props) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [data, setData] = useState<Vendor[]>([]);
  const [rawData, setRawData] = useState<Vendor[]>([]);
  const [action, setAction] = useState<string>('');
  const [actionItem, setActionItem] = useState<Vendor>(null);
  const [selectedVendors, setSelectedVendors] = useState<Vendor[]>([]);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [input, updateInput] = useState<VendorInput>({ searchName: '' });

  const addAliasToVendor = useCallback(
    (vendorName: string, alias: string, token: string) => {
      if (isSending) {
        return;
      }

      const payload: VendorUpdate = {
        action: 'Add',
        name: vendorName,
        alias,
      };

      setIsSending(true);

      fetch(`${DEVICES_ENDPOINT}/vendors`, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${token}`,
        }),
        body: JSON.stringify(payload),
      }).then((res) => {
        CheckResponseShowError(res);

        setIsSending(false);

        setRefreshData(true);
      });
    },
    [],
  );

  const filterData = (chkData: Vendor[]) => {
    if (isNotNull(chkData)) {
      let filtered: Vendor[] = [...chkData];
      let clearSelections = false;

      if (isNotNull(input.searchName) && input.searchName !== '') {
        filtered = filtered.filter((chk) =>
          chk.name.toLowerCase().startsWith(input.searchName.toLowerCase()),
        );

        clearSelections = true;
      }

      if (clearSelections) {
        setSelectedVendors([]);
      }

      setData(filtered);
    } else {
      setData([]);
    }
  };

  const setVendorSelected = (vendor: Vendor, selected: boolean) => {
    if (selected) {
      if (selectedVendors.findIndex((chk) => chk.name === vendor.name) <= -1) {
        setSelectedVendors([...selectedVendors, vendor]);
      }
    } else {
      const filtered = selectedVendors.filter(
        (chk) => chk.name !== vendor.name,
      );
      setSelectedVendors(filtered);
    }
  };

  const setInput = (updated: Partial<VendorInput>) => {
    updateInput(omitNilOrEmpty({ ...input, ...updated }));
    //setRefreshData(true);
  };

  useEffect(() => {
    filterData(rawData);
  }, [input]);

  useEffect(() => {
    if (refreshData) {
      setRefreshData(false);

      setIsLoading(true);

      const uri = `${UNAUTHENTICATED_VENDORS_ENDPOINT}`;

      fetch(uri, {
        method: 'GET',
      })
        .then((res) => (CheckResponseShowError(res) ? res.json() : []))
        .then((result: Vendor[]) => {
          const vendors = Array.isArray(result) ? result : [];

          setRawData(vendors);

          filterData(vendors);
        })
        .then(() => {
          setIsLoading(false);
        })
        .catch(() => {
          setRawData([]);

          filterData([]);

          setIsLoading(false);
        });
    }
  }, [refreshData]);

  const handleAddAliasToVendor = (
    vendorName: string,
    alias: string,
    token: string,
  ) => {
    addAliasToVendor(vendorName, alias, token);
  };

  const handleAction = (newAction: string, item: Vendor) => {
    switch (newAction) {
      case 'merge':
        if (selectedVendors.length > 1) {
          setAction(newAction);

          setActionItem(item);
        } else {
          messageApi.error('Select two or more vendors before merging!');
        }
        break;
      case 'clear_vendor_selections':
        setSelectedVendors([]);
        break;
      case 'refresh':
        setRefreshData(true);

        setAction('');

        setActionItem(null);
        break;
      default:
        setAction(newAction);

        setActionItem(item);
    }
  };

  return (
    <VendorLogoContext.Provider
      value={{
        vendorList: [],
        vendorsLoading: isLoading,
        vendorData: data,
        rawVendorData: rawData,
        action,
        actionItem,
        onAction: handleAction,
        input,
        setInput,
        selectedVendors,
        setSelectedVendor: setVendorSelected,
        addAliasToVendor: handleAddAliasToVendor,
      }}
    >
      {contextHolder}
      <div>{children}</div>
      {action === 'merge' && <MergeDialog />}
    </VendorLogoContext.Provider>
  );
};

export default VendorLogoContainer;
